import { FC, useState } from 'react'
import { ToggleButtonGroup, ToggleButton, Grid } from '@mui/material'
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker'
import { SingleInputDateRangeField } from '@mui/x-date-pickers-pro/SingleInputDateRangeField'
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFns'
import { LocalizationProvider } from '@mui/x-date-pickers-pro/LocalizationProvider'
import { DateRange } from '@mui/x-date-pickers-pro'
import { startOfWeek, endOfWeek, startOfMonth, endOfMonth, subWeeks, subMonths } from 'date-fns'

export type DateRangePreset = 'today' | 'thisWeek' | 'lastWeek' | 'currentMonth' | 'lastMonth'

const today = new Date()

export const dateRangePresets: { label: string; value: DateRangePreset; dateFrom: Date; dateTo: Date }[] = [
  { label: 'Today', value: 'today', dateFrom: today, dateTo: today },
  { label: 'This week', value: 'thisWeek', dateFrom: startOfWeek(today, { weekStartsOn: 1 }), dateTo: endOfWeek(today, { weekStartsOn: 1 }) },
  { label: 'Last week', value: 'lastWeek', dateFrom: startOfWeek(subWeeks(today, 1), { weekStartsOn: 1 }), dateTo: endOfWeek(subWeeks(today, 1), { weekStartsOn: 1 }) },
  { label: 'Current month', value: 'currentMonth', dateFrom: startOfMonth(today), dateTo: endOfMonth(today) },
  { label: 'Last month', value: 'lastMonth', dateFrom: startOfMonth(subMonths(today, 1)), dateTo: endOfMonth(subMonths(today, 1)) }
]

export const getDateRangePresetValue = (preset: DateRangePreset): DateRange<Date> => {
  const matched = dateRangePresets.find(({ value }) => value === preset) || dateRangePresets[0]
  return [matched.dateFrom, matched.dateTo]
}

interface Props {
  value: DateRange<Date>
  onChange: (range: DateRange<Date>) => void
  initialPreset?: DateRangePreset
  label?: string
}

export const DateRangePresetPicker: FC<Props> = ({ value, onChange, initialPreset = 'today', label = 'Period' }) => {
  const [preset, setPreset] = useState<DateRangePreset>(initialPreset)

  const handlePreset = (p: typeof dateRangePresets[number]) => {
    setPreset(p.value)
    onChange([p.dateFrom, p.dateTo])
  }

  return (
    <Grid container flexDirection="column">
      <Grid item>
        <ToggleButtonGroup value={preset} exclusive size="small" sx={{ mb: 2, flexWrap: 'wrap' }}>
          {dateRangePresets.map((p) => (
            <ToggleButton key={p.value} value={p.value} onClick={() => handlePreset(p)}>
              {p.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Grid>
      <Grid item>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DateRangePicker
            value={value}
            onChange={(range) => {
              setPreset(initialPreset)
              onChange(range)
            }}
            slots={{ field: SingleInputDateRangeField }}
            slotProps={{ textField: { sx: { width: 240 } } }}
            label={label}
          />
        </LocalizationProvider>
      </Grid>
    </Grid>
  )
}
